<template>
  <fullscreen-view class="email-creation">
    <template #header>
      <button class="button cancel-button" @click="$emit('closeEmailCreationModal')">cancel</button>
      <input
        :value="emailSubject"
        v-touch:email_subject
        v-has-error:email_subject
        rows="5"
        type="text"
        name="email_subject"
        class="email-subject border-can-error"
        placeholder="email subject"
        @change="updateEmailSubject($event.target.value)"/>
      <span v-if="error" class="error">{{ error }}</span>
      <span v-if="notice" class="notice">{{ notice }}</span>
      <span class="button-wrapper">
        <button class="button template-save-button" @click="beePluginInstance.saveAsTemplate()">save as template</button>
        <button class="button submit-button" @click="beePluginInstance.save()">save & schedule email</button>
      </span>
    </template>
    <template #content>
      <sb-loading-icon v-if="loading" class="loading"/>
      <div v-else id="bee-plugin-container"></div>

      <email-extension-modal
        v-if="extension.showModal"
        :extension="extension"
        :campaign="campaign"
        @rejectExtensionModal="rejectExtensionModal"
        @resolveExtensionModal="resolveExtensionModal"
      />
    </template>
  </fullscreen-view>
</template>

<script>
import { FullscreenView, Touch, HasError } from "@springbig/sb-components";
import { mapGetters } from "vuex";
import { beeAPI } from "../../../services/api/beeAPI.js";
import Bee from "@mailupinc/bee-plugin";
import EmailExtensionModal from "./email-extension-modal.vue"

export default {
  props: {
    campaign: Object,
    email: Object,
    emailSubject: String,
    selectedEmailTemplate: Object
  },

  name: "email-creation-modal",

  directives: {
    Touch, HasError
  },

  data() {
    return {
      beePluginInstance: null,
      error: null,
      loading: false,
      notice: null,
      extension: {
        buttonColor: '#128FF9',   // how the button will look
        buttonText: '',           // what the button will say
        handlers: {},             // contains handler for bee iframe
        options: {},              // contains reward parameters
        showModal: false,         // if the extension modal is open
      },
    }
  },

  async created() {
    const campaignHasOffer = [1, 5, 6].includes(parseInt(this.campaign.extension_type))
    this.extension.buttonText = campaignHasOffer ? "view your offer" : "view your wallet"

    const token = await beeAPI.getAuthToken();

    const beeConfig = {
      uid: this.currentMerchant.uuid,
      container: "bee-plugin-container",
      language: "en-US",
      customCss: `${window.location.origin}/email_template_plugin.css`,
      mergeTags: [
        {
          name: "merchant name",
          value: "%{MERCHANT_FULL_NAME}",
          previewValue: "Joe's Store"
        },
        {
          name: "merchant DBA",
          value: "%{MERCHANT_DBA}",
          previewValue: "Joe's Store DBA"
        },
        {
          name: "customer first name",
          value: "%{MEMBER_FIRST_NAME}",
          previewValue: "John"
        },  
        {
          name: "customer points balance",
          value: "%{MEMBER_BALANCE}",
          previewValue: "15"
        },
        {
          name: "customer points earned last visit",
          value: "%{MEMBER_LAST_TRANSACTION_POINTS}",
          previewValue: "20"
        },
        {
          name: "customer date of points expiration",
          value: "%{MEMBER_POINTS_EXPIRATION_DATE}",
          previewValue: "September 5, 2023"
        },
        {
          name: "customer email address",
          value: "%{MEMBER_EMAIL}",
          previewValue: "john.smith@example.com"
        },
        {
          name: "customer expiring points",
          value: "%{MEMBER_POINTS_EXPIRING}",
          previewValue: "25"
        },
        {
          name: "customer last name",
          value: "%{MEMBER_LAST_NAME}",
          previewValue: "Smith"
        },
        {
          name: "customer last purchase date",
          value: "%{MEMBER_LAST_TRANSACTION_DATE}",
          previewValue: "September 5, 2023"
        },
        {
          name: "customer last purchase spend amount",
          value: "%{MEMBER_LAST_TRANSACTION_AMOUNT}",
          previewValue: "$100.00"
        },
        {
          name: "customer medical card expiration date",
          value: "%{MEMBER_MEDICAL_CARD_EXPIRATION_DATE}",
          previewValue: "September 5, 2023"
        },
        {
          name: "customer number of days since last visit",
          value: "%{MEMBER_DAYS_SINCE_LAST_VISIT}",
          previewValue: "10"
        },
        {
          name: "customer total lifetime spend",
          value: "%{MEMBER_TOTAL_SPEND}",
          previewValue: "$300.40"
        },
        {
          name: "customer total number of visits",
          value: "%{MEMBER_TOTAL_VISITS}",
          previewValue: "12"
        },
        {
          name: "customer wallet link",
          value: "%{MEMBER_LINK}",
          previewValue: "view your wallet"
        },
        {
          name: "last transaction location",
          value: "%{MEMBER_LAST_TRANSACTION_LOCATION}",
          previewValue: "New York Shop"
        },
        {
          name: "primary location",
          value: "%{PRIMARY_LOCATION}",
          previewValue: "New York Shop"
        }
      ],
      onSaveAsTemplate: async (jsonLayout) => {
        this.notice = "saving..."
        try {
          let response = await beeAPI.postCreateMerchantEmailTemplate(jsonLayout, this.emailSubject)
          if (response.status == "200") {
            this.error = null
            this.notice = "template created successfully"
          } else {
            this.notice = null
            this.error = response.data
          }
        } catch(e){
          this.notice = null
          this.error = "error, please refresh or contact support";
        }
      },
      onSave: (jsonLayout, htmlLayout) => {
        const params = {
          "layout_json": jsonLayout,
          "layout_html": htmlLayout,
          "campaign_id": this.campaign.id
        }
        beeAPI.putUpdateEmailTemplate(this.campaign.id, jsonLayout, htmlLayout)
          .then(() => {
            // We need to wait 5 seconds in order to get the preview_image generated
            // during these 5 seconds there is a sidekiq worker that builds the preview
            this.loading = true;
            setTimeout(async () => {
              const { specific_campaign_template } = await beeAPI.getEmailTemplates(this.campaign.id);
              this.$emit("update:selectedEmailTemplate", specific_campaign_template);
              this.$emit('closeEmailCreationModal');
              this.$emit('toggleSend');
            }, 5000)
          })
          .catch((error) => {
            const errorText = error?.response?.data?.error
            if (Array.isArray(errorText) && errorText[0] && errorText[0].includes("is invalid html")) {
              this.error = "error, failed to save email content. please make changes and try again"
            } else {
              this.error = "error, please refresh or contact support";
            }
          })
      }
    }
    const extensionConfig = {
      contentDialog: {
        addOn: {
          handler: (resolve, reject, args) => {
            if (args.contentDialogId == 'trackOfferContent') {
              this.extension.showModal = true
              this.extension.handlers = {
                resolve, reject, args
              }
            } else {
              reject()
            }
          }
        }
      }
    }
    Object.assign(beeConfig, extensionConfig)
    this.beePluginInstance = new Bee(token);

    this.beePluginInstance.start(beeConfig, this.selectedEmailTemplate.layout_json);
  },

  components: {FullscreenView, EmailExtensionModal},

  computed: {
    ...mapGetters(['currentMerchant'])
  },

  methods: {
    resolveExtensionModal(payload, campaignPayload) {
      this.extension = payload
      const resolve = this.extension.handlers?.resolve;
      if (resolve) {
        resolve({
          "type": "html",
          "value": {
            html:
              `<div align="center">
                <a href="%{EXTENSION_LINK}" target="_blank" style="display:inline-block;color:#ffffff;background-color:${this.extension.buttonColor};border-radius:4px;width:auto;border:1px solid ${this.extension.buttonColor};padding-top:5px;padding-bottom:5px;font-family:Arial;text-align:center;word-break:keep-all;">
                  <span style="padding-left:20px;padding-right:20px;font-size:16px;display:inline-block;letter-spacing:normal;">
                    <span style="font-size: 16px; line-height: 2; word-break: break-word; mso-line-height-alt: 32px;">${this.extension.buttonText}</span>
                  </span>
                </a>
              </div>`,
          }
        });
      }
      this.extension.showModal = false;
      this.$emit("update:campaign", campaignPayload);
    },

    rejectExtensionModal() {
      const reject = this.extension.handlers?.reject;
      if(reject) {reject();}
      this.extension.showModal = false;
    },

    updateEmailSubject(subjectString) {
      this.$emit("updateEmailSubject", "", subjectString);
    },
  },
}
</script>

<style scoped>
.email-creation {
  display: flex;
  flex-direction: column;
}

.email-creation >>> .button.cancel-button,
.email-creation >>> .button.template-save-button {
  background-color: transparent;
  color: #128ff9;
  font-size: 18px;
  height: 58px;
  line-height: 1em;
}

.email-creation >>> .button.template-save-button {
  font-family: "AileronLight";
}

.email-creation >>> .button.cancel-button {
  font-family: "AileronThin";
}

.email-creation >>> .button-wrapper {
  min-width: 380px;
}

.email-creation >>> header {
  align-items: center;
  background-color: #128ff933;
  display: flex;
  flex-direction: row;
  justify-content: space-between !important;
  height: 100px;
  padding: 0 1.5rem !important;
}

.email-creation >>> .content {
  display: flex;
  flex-flow: row wrap;
  height: 100%;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  width: 100%;
}

#bee-plugin-container {
  flex: 1;
  height: 100%;
}

.email-creation .loading {
  height: auto;
  width: 450px;
}

.email-creation >>> .email-subject {
  width: 500px;
  margin: auto auto auto calc(49.3% - 539px);
}

.email-creation >>> .button.submit-button {
  font-size: larger;
  padding: 0.85rem 0.6rem;
}
</style>